import type React from "react";

import { chatConsult, leadWithAi, LeadWithAiResponse } from "@src/api";
import useUser from "@src/hook/useUser";
import { initPage } from "@src/state";
import { useRouter } from "next/router";
import { sendSlackError } from "@src/api/error";
import {
	aiConsultResultDataAtom,
	aiMatchUuidAtom,
	globalSnackbarState,
	messagesAtom,
} from "@src/state";
import { UserInfo } from "@src/types/userInfo";
import { Bot, CheckCircle, Send, User, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import TagManager from "react-gtm-module";
import { useQueryClient } from "react-query";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { v4 } from "uuid";
import { Button } from "./base/button";
import { Card } from "./base/card";
import { Textarea } from "./base/textarea";

// ANSI 이스케이프 코드를 제거하는 함수
const removeAnsiCodes = (text: string): string => {
	// ANSI 이스케이프 시퀀스 제거 (예: [1m, [0m, [31m 등)
	return text.replace(/\u001b\[[0-9;]*m/g, "");
};

// URL을 링크로 변환하는 함수
const convertUrlsToLinks = (text: string, isUserMessage = false) => {
	// 먼저 ANSI 이스케이프 코드 제거
	const cleanedText = removeAnsiCodes(text);
	
	// URL 패턴 수정: 도메인과 경로만 포함하고 뒤에 붙은 한글은 제외
	const urlRegex =
		/(https?:\/\/[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+[^\s가-힣]*)/g;

	if (!urlRegex.test(cleanedText)) return cleanedText;

	return cleanedText.split(urlRegex).map((part, index) => {
		if (part.match(urlRegex)) {
			return (
				<a
					key={index}
					href={part}
					target="_blank"
					rel="noopener noreferrer"
					className={
						isUserMessage
							? "text-white hover:underline"
							: "text-blue-600 hover:underline"
					}
				>
					{part}
				</a>
			);
		}
		return part;
	});
};

interface ChatInterfaceProps {
	userInfo: UserInfo;
	onClose: () => void;
	isOpen: boolean;
	defaultMessage?: string;
}

export function ChatInterface({ userInfo, onClose, isOpen, defaultMessage }: ChatInterfaceProps) {
	const messages = useRecoilValue(messagesAtom);
	const setMessages = useSetRecoilState(messagesAtom);
	const [currentInput, setCurrentInput] = useState("");
	const setResultData = useSetRecoilState(aiConsultResultDataAtom);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const resultData = useRecoilValue(aiConsultResultDataAtom);

	const queryClient = useQueryClient();
	const { user } = useUser();
	const router = useRouter();
	const setInitPage = useSetRecoilState(initPage);

	const setSnackbareState = useSetRecoilState(globalSnackbarState);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isApplyLoading, setIsApplyLoading] = useState<boolean>(false);

	const aiMatchUuid = useRecoilValue(aiMatchUuidAtom);
	const setAiMatchUuid = useSetRecoilState(aiMatchUuidAtom);

	useEffect(() => {
		if (isOpen) {
			setAiMatchUuid(v4());
			setMessages([]);
		}
	}, [isOpen]);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const handleClose = () => {
		// 상태 초기화
		setMessages([]);
		setIsApplyLoading(false);
		onClose();
	};

	const handleSendMessage = async () => {
		if (isLoading) return;
		setIsLoading(true);
		const lastChatHistory = [...messages];
		const lastMessage = currentInput;
		setMessages([
			...lastChatHistory,
			{
				role: "user",
				content: [
					{
						type: "input_text",
						text: lastMessage,
					},
				],
			},
		]);
		setCurrentInput("");
		const resultResponse = await chatConsult(lastMessage, messages, aiMatchUuid, userInfo);
		const result = resultResponse;
		if (result) {
			// 프롬프트 주입 공격 방지
			try {
				const chatResultToFindPromptInject = result[(result as []).length - 1];
				if (
					(JSON.parse(chatResultToFindPromptInject.content[0].text) as any)
						.isDetectedPromptInjectedAttack
				) {
					setMessages([]);
					setAiMatchUuid(v4());
					setIsLoading(false);
					sendSlackError({
						message: `채팅 문제발생: ${lastMessage}\n\n회사명: ${userInfo.companyName}\n\n이름: ${userInfo.name}\n이메일: ${userInfo.email}\n전화번호: ${userInfo.phone}`,
						title: "AI 채팅 문제 발생",
					});
					handleClose();
					setSnackbareState("정책 위반으로 대화를 종료합니다");
					return;
				}
			} catch (error) {
				// console.error(error);
			}
			setMessages([
				...lastChatHistory,
				{
					role: "user",
					content: [
						{
							type: "input_text",
							text: lastMessage,
						},
					],
				},
				...result,
			]);
			setResultData(JSON.parse(result[result.length - 1].content[0].text));
		} else {
			setMessages([...lastChatHistory]);
		}
		setIsLoading(false);
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	const handleSubmitRequest = async (callback?: () => void, isAiLead?: boolean) => {
		if (isApplyLoading) return;
		setIsApplyLoading(true);
		try {
			const result: LeadWithAiResponse | null = await leadWithAi({
				userInfo: {
					name: userInfo.name,
					email: userInfo.email,
					phone: userInfo.phone.replace(/-/g, ""),
					companyName: userInfo.companyName,
					industry: userInfo.industry,
					companySize: userInfo.companySize,
					position: userInfo.position,
					route: userInfo.route,
					routeDetail: userInfo.routeDetail,
					inviteCode: userInfo.inviteCode,
				},
				matchUuid: aiMatchUuid,
				isAiLead,
			});

			// 기존고객사 분기 처리
			if (result && result.isExistingUser && result.matchUuid) {
				if (user.isLogin) {
					// 로그인 상태: 업무요청서 수정 페이지로 이동
					if (isAiLead === false) {
						setSnackbareState(
							"신청해주셔서 감사드립니다. 빠른 시일내에 컨설턴트가 전화 드리도록 하겠습니다.\n전화전 직접 수정도 가능하시니 기입 가능한 부분은 작성 부탁드립니다.",
						);
					} else {
						setSnackbareState("매칭 신청서 수정 및 제출을 진행해주세요.");
					}
					await queryClient.invalidateQueries("getMatchs");
					router.push(`/match/my/?st=1&mid=${result.matchUuid}&edid=-&isAiLead=${isAiLead ? "true" : "false"}`);
				} else {
					// 비로그인 상태: 모두 팝업 표시
					setInitPage(`/match/my/?st=1&mid=${result.matchUuid}&edid=-&isAiLead=${isAiLead ? "true" : "false"}`);
					if (isAiLead === true) {
						// isAiLead === true: 기존 팝업 메시지
						router.push(`/signin?type=client&showInfoDialog=true&dialogType=default&isAiLead=true`);
					} else {
						// isAiLead === false: 전화 상담 요청 메시지
						router.push(`/signin?type=client&showInfoDialog=true&dialogType=phoneConsult&isAiLead=false`);
					}
				}
				handleClose();
				// 기존고객사는 여기서 함수 종료
				return;
			}

			// 무비번 & 신규 고객사: 백엔드가 내려준 registerUrl로 회원가입 이동
			if (result?.registerUrl) {
				// 쿼리 파라미터로 메시지 타입 전달
				let url = new URL(result.registerUrl);
				
				// local 환경에서는 localhost로 변환
				if (url.hostname === "1point.kr" && window.location.hostname === "localhost") {
					url = new URL(url.pathname + url.search, `http://${window.location.host}`);
				}
				
				if (isAiLead === false) {
					url.searchParams.set("dialogType", "phoneConsult");
				} else {
					url.searchParams.set("dialogType", "signup");
				}
				// isAiLead 파라미터 추가 (회원가입 후 스낵바 표시용)
				url.searchParams.set("isAiLead", isAiLead ? "true" : "false");
				window.location.href = url.toString();
				return;
			}

			// fallback: isAiLead에 따라 다른 메시지 표시
			if (isAiLead === false) {
				setSnackbareState(
					"신청해주셔서 감사드립니다. 빠른 시일내에 컨설턴트가 전화 드리도록 하겠습니다.\n전화전 직접 수정도 가능하시니 기입 가능한 부분은 작성 부탁드립니다.",
				);
			} else {
				setSnackbareState(
					"상담 신청이 완료되었습니다.\n담당 컨설턴트가 영업일 기준 2일 내로 전화 드리겠습니다.",
				);
			}
			const tagManagerArgs = {
				dataLayer: {
					event: `client-apply-end`,
					event_id: aiMatchUuid,
				},
			};
			if (!window.localStorage.getItem("accessToken")) {
				TagManager.dataLayer(tagManagerArgs);
			}
			if (callback) {
				callback();
			}
			queryClient.refetchQueries(["getMatchs"]);
			handleClose();
		} catch (error) {
			sendSlackError({
				message: `회사명: ${userInfo.companyName}\n\n이름: ${userInfo.name}\n이메일: ${userInfo.email}\n전화번호: ${userInfo.phone}`,
				title: "AI 신청 문제 발생",
			});
		} finally {
			setIsApplyLoading(false);
		}
	};

	return (
		<div
			className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center md:p-4 z-[99999] ${
				isOpen ? "block" : "hidden"
			}`}
		>
			<Card className="w-full h-full md:max-w-[1200px] md:h-[1200px] md:max-h-[80vh] flex flex-col bg-white">
				{/* Header */}
				<div className="flex items-center justify-between p-4 border-b">
					<div className="flex items-center space-x-3">
						<div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
							<Bot className="w-6 h-6 text-white" />
						</div>
						<div>
							<h3 className="font-semibold">원포인트 AI 컨설턴트</h3>
							<p className="text-sm text-slate-500">전문가 매칭 상담</p>
						</div>
					</div>
					<Button variant="ghost" size="sm" onClick={handleClose}>
						<X className="w-4 h-4" />
					</Button>
				</div>

				{/* Messages */}
				<div className="flex-1 overflow-y-auto p-4 space-y-4">
					<div className="flex items-start space-x-2 max-w-[80%]">
						<div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center shrink-0">
							<Bot className="w-4 h-4 text-slate-600" />
						</div>
						<div className="bg-slate-100 rounded-lg p-3">
							<p className="text-sm">
								{defaultMessage
									? defaultMessage
									: `안녕하세요 원포인트 AI 컨설턴트입니다. 🙂

지금부터 전문가 매칭 정확도를 2배 높이는 업무 요청서 초안을 만들어 드릴게요.
혹시 내용이 부족하면 컨설턴트가 연락을 드리니 너무 걱정 마시고 편하게 완성해주세요.

현재 상황이나 고민하고 계신 부분이 무엇이신가요?`}
							</p>
						</div>
					</div>
					{messages
						.filter((message) => message.type === "message" || message.role === "user")
						.map((message) => (
							<div
								key={message.id}
								className={`flex ${
									message.role === "user" ? "justify-end" : "justify-start"
								}`}
							>
								<div
									className={`flex items-start space-x-2 max-w-[80%] ${
										message.role === "user"
											? "flex-row-reverse space-x-reverse"
											: ""
									}`}
								>
									<div
										className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
											message.role === "user" ? "bg-blue-600" : "bg-slate-200"
										}`}
									>
										{message.role === "user" ? (
											<User className="w-4 h-4 text-white" />
										) : (
											<Bot className="w-4 h-4 text-slate-600" />
										)}
									</div>
									<div
										className={`rounded-lg p-3 ${
											message.role === "user"
												? "bg-blue-600 text-white"
												: "bg-slate-100 text-slate-800"
										}`}
									>
										<p className="text-sm">
											{message.role === "assistant"
												? convertUrlsToLinks(
														(JSON.parse(message.content[0].text) as any)
															.nextMessage,
												  )
												: convertUrlsToLinks(message.content[0].text, true)}
										</p>
									</div>
								</div>
							</div>
						))}

					{isLoading && (
						<div className="flex justify-start">
							<div className="flex items-start space-x-2 max-w-[80%]">
								<div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
									<Bot className="w-4 h-4 text-slate-600" />
								</div>
								<div className="bg-slate-100 rounded-lg p-3">
									<div className="flex space-x-1">
										<div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
										<div
											className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
											style={{ animationDelay: "0.1s" }}
										></div>
										<div
											className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
											style={{ animationDelay: "0.2s" }}
										></div>
									</div>
								</div>
							</div>
						</div>
					)}
					<div ref={messagesEndRef} />
				</div>
				<p className="text-xs text-slate-500 text-center mb-2">
					AI는 실수를 할 수 있습니다. 중요한 정보는 재차 확인하세요!
				</p>
				<div className="p-4 border-t">
					<div className="flex space-x-2">
						<Textarea
							value={currentInput}
							onChange={(e) => setCurrentInput(e.target.value)}
							onKeyPress={handleKeyPress}
							placeholder="메시지를 입력하세요..."
							className="flex-1"
						/>
						<Button
							onClick={handleSendMessage}
							isLoading={isLoading}
							disabled={!currentInput.trim()}
							className="bg-blue-600 hover:bg-blue-700"
						>
							<Send className="w-4 h-4" />
						</Button>
					</div>
				</div>
				<div className="p-4 border-t bg-slate-50">
					<div className="flex flex-col md:flex-row gap-3">
						<Button
							onClick={() => {
								handleSubmitRequest(() => {
									handleClose();
								}, true);
							}}
							isLoading={isApplyLoading}
							className="w-full md:w-[70%] bg-blue-600 hover:bg-blue-700 text-white"
							size="lg"
							id="consult-submit-request-button"
						>
							<CheckCircle className="w-5 h-5 mr-2" />
							{defaultMessage
								? "AI 초안으로 매칭 시작하기 (매칭 정확도 2배 ↑)"
								: "AI 초안으로 매칭 시작하기 (매칭 정확도 2배 ↑)"}
						</Button>
						<Button
							onClick={() => {
								handleSubmitRequest(() => {
									handleClose();
								}, false);
							}}
							isLoading={isApplyLoading}
							variant="outline"
							className="w-full md:w-[30%] border-gray-1200 hover:bg-gray-100"
							size="lg"
							style={{
								color: "#666666",
							}}
						>
							{defaultMessage ? "전화 상담 요청 후 대기하기" : "전화 상담 요청 후 대기하기"}
						</Button>
					</div>
					<p className="text-xs text-slate-500 text-center mt-3">
						버튼을 클릭하시면 수정 페이지로 이동합니다.
					</p>
				</div>
			</Card>
		</div>
	);
}
